import axios from 'axios';
import i18n from '../i18n/config';

// Create axios instance with base configuration
const api = axios.create({
  baseURL: process.env.REACT_APP_STRAPI_API_URL || 'http://localhost:1337',
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second

/**
 * Delay function for retry logic
 * @param {number} ms - Milliseconds to delay
 * @returns {Promise}
 */
const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Check if error is retryable
 * @param {Error} error - Axios error object
 * @returns {boolean}
 */
const isRetryableError = (error) => {
  // Retry on network errors or 5xx server errors
  if (!error.response) {
    return true; // Network error
  }
  
  const status = error.response.status;
  return status >= 500 && status < 600;
};

/**
 * Retry request with exponential backoff
 * @param {Function} fn - Function to retry
 * @param {number} retries - Number of retries remaining
 * @returns {Promise}
 */
const retryRequest = async (fn, retries = MAX_RETRIES) => {
  try {
    return await fn();
  } catch (error) {
    if (retries > 0 && isRetryableError(error)) {
      const delayTime = RETRY_DELAY * (MAX_RETRIES - retries + 1); // Exponential backoff
      console.log(`Retrying request in ${delayTime}ms... (${retries} retries left)`);
      await delay(delayTime);
      return retryRequest(fn, retries - 1);
    }
    throw error;
  }
};

// Request interceptor for logging and locale
api.interceptors.request.use(
  (config) => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] API Request: ${config.method?.toUpperCase()} ${config.url}`);
    
    // Add locale parameter to all requests
    // Priority: explicit locale in params > URL path language > i18n.language > 'en'
    if (!config.params?.locale) {
      // Read language from URL path (most reliable source during navigation)
      const pathLang = window.location.pathname.split('/')[1];
      const supportedLangs = ['en', 'vi', 'pl'];
      const currentLang = supportedLangs.includes(pathLang) ? pathLang : (i18n.language || 'en');
      config.params = {
        ...config.params,
        locale: currentLang
      };
    }
    
    // Log query parameters if present
    if (config.params && Object.keys(config.params).length > 0) {
      console.log('Query params:', config.params);
    }
    
    return config;
  },
  (error) => {
    console.error('API Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for logging and error handling
api.interceptors.response.use(
  (response) => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] API Response: ${response.status} ${response.config.url}`);
    
    // Log response data size
    if (response.data) {
      const dataSize = JSON.stringify(response.data).length;
      console.log(`Response size: ${(dataSize / 1024).toFixed(2)} KB`);
    }
    
    return response;
  },
  async (error) => {
    const timestamp = new Date().toISOString();
    console.error(`[${timestamp}] API Response Error:`, error.response?.data || error.message);
    
    // Handle specific error cases
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          console.error('Bad Request:', data?.error?.message || 'Invalid request parameters');
          break;
        case 401:
          console.error('Unauthorized:', data?.error?.message || 'Authentication required');
          break;
        case 403:
          console.error('Forbidden:', data?.error?.message || 'Access denied');
          break;
        case 404:
          console.error('Not Found:', data?.error?.message || 'Resource not found');
          break;
        case 500:
          console.error('Server Error:', data?.error?.message || 'Internal server error');
          break;
        case 503:
          console.error('Service Unavailable:', data?.error?.message || 'Service temporarily unavailable');
          break;
        default:
          console.error(`Error ${status}:`, data?.error?.message || data);
      }
      
      // Create user-friendly error message
      error.userMessage = getUserFriendlyErrorMessage(status, data);
    } else if (error.request) {
      // Request made but no response received
      console.error('No response from server - Network error or timeout');
      error.userMessage = 'Unable to connect to the server. Please check your internet connection and try again.';
    } else {
      // Something else happened
      console.error('Request setup error:', error.message);
      error.userMessage = 'An unexpected error occurred. Please try again.';
    }
    
    return Promise.reject(error);
  }
);

/**
 * Get user-friendly error message based on status code
 * @param {number} status - HTTP status code
 * @param {Object} data - Error response data
 * @returns {string} User-friendly error message
 */
const getUserFriendlyErrorMessage = (status, data) => {
  const defaultMessages = {
    400: 'Invalid request. Please check your input and try again.',
    401: 'You need to be logged in to access this content.',
    403: 'You do not have permission to access this content.',
    404: 'The requested content could not be found.',
    500: 'Something went wrong on our end. Please try again later.',
    503: 'The service is temporarily unavailable. Please try again in a few moments.',
  };
  
  // Use custom message from API if available, otherwise use default
  return data?.error?.message || defaultMessages[status] || 'An error occurred. Please try again.';
};

/**
 * Wrapper for API requests with retry logic
 * @param {Function} requestFn - Axios request function
 * @returns {Promise}
 */
export const apiWithRetry = (requestFn) => {
  return retryRequest(requestFn);
};

/**
 * Helper function to fetch localized content
 * @param {string} endpoint - API endpoint
 * @param {string|null} locale - Optional locale override (defaults to current i18n language)
 * @param {Object} params - Additional query parameters
 * @returns {Promise} API response data
 */
export const fetchLocalizedContent = async (endpoint, locale = null, params = {}) => {
  const lang = locale || i18n.language || 'en';
  const response = await api.get(endpoint, {
    params: {
      ...params,
      locale: lang
    }
  });
  return response.data;
};

export default api;
